import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listSessionsByBatch, listSessionsCalendar, setSessionStatus, reverseSession } from "../../api/sessions";
import { listLevels } from "../../api/levels";
import { formatLevelLabel } from "../../utils/formatLevelLabel";
import { listBatches } from "../../api/batches";
import API from "../../api";
import { getNormalizedRole } from "../../utils/auth";
import { useCurrentUser } from "../../context/UserContext";
import SessionForm from "../sessions/SessionForm";
import { ddmmyyyyToIso, formatDateTime, formatDate, formatTime } from "../../utils/date";
import UserLink from "../common/UserLink";
import PageHeaderSection from "../layout/PageHeaderSection";
import { ListLoadingSpinner } from "../layout/ListPageLayout";
import WalkInSessionModal from "./WalkInSessionModal";
import IndividualSessionModal from "./IndividualSessionModal";
import UpdateSessionNotesModal from "./UpdateSessionNotesModal";
import { showConfirm } from '../../utils/popup';
import { formatCurrency } from '../../utils/currency';
import { TextInput, SelectInput, TextareaInput } from '../common/form';
import { getClassroomStatus } from '../../api/classroom';
import SessionHostAction from '../classroom/SessionHostAction';
import { userDisplayName, localizeBatchName } from '../../utils/displayNames';
import { apiErrorMessage } from '../../utils/apiError';


const STATUS_OPTIONS = ["PENDING", "COMPLETED", "CANCELLED"];

const useDebounced = (value, delay = 200) => {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
};

const fmtDT = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return formatDateTime(d);
    } catch {
        return String(iso);
    }
};

const formatTimeOnly = (iso) => (iso ? formatTime(iso) : "—");

const formatSessionDate = (iso) => (iso ? formatDate(iso) : "—");

const formatSessionTiming = (startTime, endTime) => {
    if (!startTime || !endTime) return "—";
    const startDate = formatSessionDate(startTime);
    const endDate = formatSessionDate(endTime);
    const start = formatTimeOnly(startTime);
    const end = formatTimeOnly(endTime);

    return startDate === endDate
        ? `${startDate} • ${start} → ${end}`
        : `${startDate} ${start} → ${endDate} ${end}`;
};

const NameWithId = ({ name, id }) => {
    if (name && id != null) return <>{name} (#{id})</>;
    if (name) return <>{name}</>;
    if (id != null) return <>#{id}</>;
    return <>—</>;
};

const getId = (o) => o?.id ?? o?.userId ?? o?.coachId ?? o?.levelId ?? o?.batchId ?? null;
const fullName = (o, t) =>
    [o?.firstName, o?.middleName, o?.lastName].filter(Boolean).join(" ").trim() ||
    o?.name ||
    o?.username ||
    (getId(o) != null ? userDisplayName(null, getId(o), t) : "");

/** fetch list of COACH users depending on role; returns array */
async function fetchCoachesDirectory(role) {
    const tries = [];
    if (role === "ADMIN") {
        tries.push(["/admin/users"]);
    } else if (role === "MANAGER") {
        // Try manager endpoint first (may not exist), then admin as a fallback
        tries.push(["/manager/users"], ["/admin/users"]);
    } else {
        // Coach has no directory endpoint; avoid /admin/users (403 console noise).
        return [];
    }

    for (const [base] of tries) {
        try {
            const res = await API.get(base, { params: { role: "COACH", page: 0, size: 500 } });
            const list = res?.data?.content ?? res?.data ?? [];
            if (Array.isArray(list)) return list;
        } catch {
            // continue to next try
        }
    }
    return [];
}

const baseFor = (role) => (role === "ADMIN" ? "/admin" : role === "MANAGER" ? "/manager" : "/coach");

/**
 * Unified Sessions List — supports:
 *  - filter by Level/Batch (like sidebar 'Sessions')
 *  - inline Status change + 'Mark Attendance'
 *  - Edit Session (opens SessionForm)
 *  - Create Session (+ optional recurring) when a Batch is selected
 *  - optional initialBatchId to preselect from Batches->Sessions
 */
const SessionsList = ({ role: roleProp, initialBatchId }) => {
    const { t } = useTranslation();
    const role = getNormalizedRole(roleProp);
    const { user } = useCurrentUser();
    const navigate = useNavigate();
    const location = useLocation();
    const calendarDateFromNav = location.state?.calendarDate || null;

    const statusLabel = (status) => {
        const s = String(status ?? "").toUpperCase();
        if (s === "PENDING") return t("sessions.statusPending");
        if (s === "COMPLETED") return t("sessions.statusCompleted");
        if (s === "CANCELLED") return t("sessions.statusCancelled");
        return status;
    };

    // Load saved filters from localStorage
    const loadSavedFilters = () => {
        try {
            const saved = localStorage.getItem(`sessions-filters-${role}`);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    levelId: parsed.levelId || "",
                    batchId: parsed.batchId || "",
                    statusFilter: parsed.statusFilter || "",
                    sortBy: parsed.sortBy || "date-asc",
                    query: parsed.query || ""
                };
            }
        } catch (e) {
            console.error("Failed to load saved filters:", e);
        }
        return {
            levelId: "",
            batchId: "",
            statusFilter: "",
            sortBy: "date-asc",
            query: ""
        };
    };

    const savedFilters = loadSavedFilters();

    const [levels, setLevels] = useState([]);
    const [levelId, setLevelId] = useState(savedFilters.levelId);
    const [batches, setBatches] = useState([]);
    const [batchId, setBatchId] = useState(savedFilters.batchId);
    const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter); // Status filter
    const [sortBy, setSortBy] = useState(savedFilters.sortBy); // Sort option - default to oldest first

    const [rows, setRows] = useState([]);
    const [statusDraft, setStatusDraft] = useState({});
    const [savingId, setSavingId] = useState(null);

    const [query, setQuery] = useState(savedFilters.query);
    const [dateFilter, setDateFilter] = useState(calendarDateFromNav);
    const dq = useDebounced(query);

    useEffect(() => {
        if (calendarDateFromNav) setDateFilter(calendarDateFromNav);
    }, [calendarDateFromNav]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filtersLoadError, setFiltersLoadError] = useState(false);

    // Edit/Create modal
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);

    // Pagination
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // When preselecting by batch, we learn levelId from the batch details
    const [prefillBatchId, setPrefillBatchId] = useState(initialBatchId || null);

    // Reversal modal state
    const [showReversalModal, setShowReversalModal] = useState(false);
    const [selectedSession, setSelectedSession] = useState(null);
    const [reversalReason, setReversalReason] = useState("");
    const [reversingId, setReversingId] = useState(null);

    // Walk-in session modal state
    const [showWalkInModal, setShowWalkInModal] = useState(false);

    // Individual session modal state
    const [showIndividualModal, setShowIndividualModal] = useState(false);

    // Update session notes modal state
    const [showUpdateNotesModal, setShowUpdateNotesModal] = useState(false);
    const [sessionForNotesUpdate, setSessionForNotesUpdate] = useState(null);

    // Ref to store the loadSessions function so we can call it from the modal
    const loadSessionsRef = useRef(null);

    // Save filters to localStorage whenever they change (except on initial mount with initialBatchId)
    useEffect(() => {
        // Don't save if we're using initialBatchId prefill (let it complete first)
        if (prefillBatchId) return;

        const filters = {
            levelId,
            batchId,
            statusFilter,
            sortBy,
            query
        };
        try {
            localStorage.setItem(`sessions-filters-${role}`, JSON.stringify(filters));
        } catch (e) {
            console.error("Failed to save filters:", e);
        }
    }, [levelId, batchId, statusFilter, sortBy, query, role, prefillBatchId]);

    useEffect(() => {
        // Warm classroom status so Start Zoom failures show connection context promptly
        getClassroomStatus().catch(() => {});
    }, []);

    useEffect(() => {
        let alive = true;
        const boot = async () => {
            setLoading(true);
            setError(null);
            setFiltersLoadError(false);
            try {
                // Load levels
                const res = await listLevels(role, { page: 0, size: 500 });
                const vals = res?.data?.content ?? res?.data ?? [];
                if (!alive) return;
                setLevels(vals);

                // If a batch is preselected, fetch ALL batches and find the one we need
                if (initialBatchId) {
                    try {
                        // Fetch all batches for this role (Manager/Coach might not support filtering by ID)
                        const br = await listBatches(role, { page: 0, size: 1000 });
                        const arr = br?.data?.content ?? br?.data ?? [];

                        // Find the specific batch by ID
                        const targetBatch = Array.isArray(arr)
                            ? arr.find(b => (b.id === Number(initialBatchId) || b.batchId === Number(initialBatchId)))
                            : null;

                        if (targetBatch) {
                            const lvlId =
                                targetBatch?.levelId ??
                                targetBatch?.level_id ??
                                targetBatch?.level?.id ??
                                targetBatch?.level?.levelId ??
                                targetBatch?.level?.level_id;

                            if (lvlId != null) {
                                setLevelId(String(lvlId));
                                setPrefillBatchId(Number(initialBatchId));
                            } else if (alive) {
                                setFiltersLoadError(true);
                            }
                        } else if (alive) {
                            setFiltersLoadError(true);
                        }
                    } catch (err) {
                        console.error("Failed to load batch details:", err);
                        if (alive) setFiltersLoadError(true);
                    }
                }
            } catch (e) {
                console.error("Failed to load levels:", e);
                if (alive) {
                    setLevels([]);
                    setFiltersLoadError(true);
                }
            } finally {
                if (alive) setLoading(false);
            }
        };
        boot();
        return () => { alive = false; };
    }, [role, initialBatchId]);

    // Load batches when level changes (role-aware) — backend uses OPTIONAL request param ?levelId=
    useEffect(() => {
        let alive = true;
        const loadBatches = async () => {
            // Store current batchId before clearing to restore it if valid
            const currentBatchId = batchId;

            setBatches([]);
            setBatchId("");
            setRows([]);
            setStatusDraft({});

            // ✅ If no level selected, do NOT fetch batches and keep dropdown empty
            if (!levelId) {
                return;
            }

            try {
                const params = { page: 0, size: 500, levelId: Number(levelId) };
                const res = await listBatches(role, params);
                const vals = res?.data?.content ?? res?.data ?? [];
                if (!alive) return;
                setFiltersLoadError(false);
                setBatches(vals);

                // Priority 1: If coming from prefill (navigation from batch page)
                if (prefillBatchId) {
                    const found = vals.find(b => (b.id ?? b.batchId) === prefillBatchId);
                    if (found) {
                        setBatchId(String(prefillBatchId));
                    }
                    setPrefillBatchId(null); // use once
                }
                // Priority 2: Restore saved batchId if it exists in loaded batches
                else if (currentBatchId) {
                    const found = vals.find(b => String(b.id ?? b.batchId) === String(currentBatchId));
                    if (found) {
                        setBatchId(currentBatchId);
                    }
                }
            } catch (e) {
                console.error(e);
                if (alive) {
                    setBatches([]);
                    setFiltersLoadError(true);
                }
            }
        };
        loadBatches();
        return () => { alive = false; };
    }, [role, levelId]); // re-fetch when level filter changes

    // Calendar date view: show all sessions on that day without picking a batch first
    useEffect(() => {
        if (!dateFilter || batchId) return;
        let alive = true;
        const loadByDate = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await listSessionsCalendar(role, dateFilter, dateFilter);
                const list = Array.isArray(res?.data) ? res.data : [];
                if (!alive) return;
                setRows(list);
                setTotalPages(1);
                setTotalElements(list.length);
                const sd = {};
                for (const r of list) sd[r.sessionId] = r.status ?? "PENDING";
                setStatusDraft(sd);
            } catch (e) {
                setError(apiErrorMessage(e, t("sessions.loadDateFailed")));
            } finally {
                if (alive) setLoading(false);
            }
        };
        loadByDate();
        return () => { alive = false; };
    }, [role, dateFilter, batchId]);

    // Load sessions when batch, page, sortBy, or statusFilter changes
    useEffect(() => {
        let alive = true;
        const loadSessions = async () => {
            setRows([]);
            setStatusDraft({});
            if (!batchId) return;
            if (dateFilter && !batchId) return;
            setLoading(true);
            setError(null);
            try {
                // Build params with sorting and filtering
                const params = { page, size };
                if (sortBy) params.sortBy = sortBy;
                if (statusFilter) params.status = statusFilter;

                const res = await listSessionsByBatch(role, batchId, params);
                const data = res?.data ?? {};
                // Handle paginated response
                const list = data.content ?? data ?? [];
                const normalized = Array.isArray(list) ? list : [];
                if (!alive) return;
                setRows(normalized);
                setTotalPages(data.totalPages ?? 1);
                setTotalElements(data.totalElements ?? normalized.length);
                const sd = {};
                for (const r of normalized) sd[r.sessionId] = r.status ?? "PENDING";
                setStatusDraft(sd);
                setError(null);
            } catch (e) {
                console.error(e);
                setError(apiErrorMessage(e, t("sessions.loadFailed")));
            } finally {
                if (alive) setLoading(false);
            }
        };

        // Store the function in a ref so it can be called from the modal
        loadSessionsRef.current = loadSessions;

        loadSessions();
        return () => { alive = false; };
    }, [role, batchId, page, size, sortBy, statusFilter]);

    const coaches = useMemo(() => {
        // prefetch coaches async (not blocking UI)
        return null;
    }, []);

    useEffect(() => {
        let alive = true;
        const go = async () => {
            const list = await fetchCoachesDirectory(role);
            if (!alive) return;
            const map = {};
            for (const c of list) {
                const id = c.userId ?? c.id;
                if (id != null) map[id] = fullName(c, t);
            }
            setCoachMap(map);
        };
        go();
        return () => { alive = false; };
    }, [role]);
    const [coachMap, setCoachMap] = useState({});

    const filtered = useMemo(() => {
        let result = rows;

        // Filter by coach role - coaches should only see their own sessions
        if (role === "COACH" && user?.id) {
            result = result.filter((r) => {
                const sessionCoachId = r.coachId || r.coach_id || r.coach?.id || r.coach?.userId;
                return sessionCoachId === user.id || sessionCoachId === user.userId;
            });
        }

        if (dateFilter) {
            result = result.filter((r) => r.startTime && String(r.startTime).slice(0, 10) === dateFilter);
        }

        // Only apply client-side search filtering (status and sorting handled by backend)
        const tokens = dq.trim().toLowerCase().split(/\s+/).filter(Boolean);
        if (tokens.length > 0) {
            result = result.filter((r) => {
                const hay = [
                    r.sessionId, r.levelId, r.levelName, r.batchId, r.batchName,
                    r.coachId, coachMap[r.coachId], r.status, r.startTime, r.endTime
                ].filter(Boolean).join(" ").toLowerCase();
                return tokens.every((t) => hay.includes(t));
            });
        }

        return result;
    }, [rows, dq, coachMap, role, user, dateFilter]);

    // Use filtered rows directly (pagination is handled by backend)
    const pageRows = filtered;

    const returnPath = location.pathname + location.search;

    const gotoMark = (s) => {
        const base = role === "ADMIN" ? "/admin" : role === "MANAGER" ? "/manager" : "/coach";
        navigate(`${base}/sessions/${s.sessionId}/attendance`, {
            state: {
                from: returnPath,
                batchId: Number(batchId) || s.batchId,
                session: {
                    sessionId: s.sessionId,
                    levelId: s.levelId,
                    levelName: s.levelName,
                    batchId: s.batchId,
                    batchName: s.batchName,
                    coachId: s.coachId,
                    coachFullName: coachMap[s.coachId],
                    startTime: s.startTime,
                    endTime: s.endTime,
                    status: s.status,
                },
            },
        });
    };

    const gotoView = (s) => {
        const base = role === "ADMIN" ? "/admin" : role === "MANAGER" ? "/manager" : "/coach";
        navigate(`${base}/sessions/${s.sessionId}/attendance/view`, {
            state: {
                from: returnPath,
                batchId: Number(batchId) || s.batchId,
                sessionId: s.sessionId,
                levelId: s.levelId,
                levelName: s.levelName,
                batchId: s.batchId,
                batchName: s.batchName,
                coachId: s.coachId,
                coachFullName: coachMap[s.coachId] ?? s.coachFullName ?? "",
                status: s.status,
                startTime: s.startTime,
                endTime: s.endTime,
            },
        });
    };

    // ---------- NEW: minimal attendance check helper (no new imports) ----------
    const hasAnyAttendance = async (sessionId) => {
        const base = baseFor(role);
        const tries = [
            { url: `${base}/attendance/by-session/${sessionId}`, params: null },
            { url: `${base}/attendance`, params: { sessionId } },
            { url: `${base}/sessions/${sessionId}/attendance`, params: null },
        ];
        let sawTransportFailure = false;
        let anySucceeded = false;
        for (const t of tries) {
            try {
                const res = t.params
                    ? await API.get(t.url, { params: t.params })
                    : await API.get(t.url);
                anySucceeded = true;
                const data = res?.data;
                if (Array.isArray(data) && data.length > 0) return true;
                if (Array.isArray(data?.content) && data.content.length > 0) return true;
            } catch {
                sawTransportFailure = true;
            }
        }
        if (!anySucceeded && sawTransportFailure) return null;
        return false;
    };
    // --------------------------------------------------------------------------

    const onChangeStatusDraft = (sessionId, val) => {
        setStatusDraft(prev => ({ ...prev, [sessionId]: val }));
    };

    const onSaveStatus = async (session) => {
        const newStatus = statusDraft[session.sessionId] ?? session.status ?? "PENDING";
        if (String(newStatus).toUpperCase() === String(session.status).toUpperCase()) return;

        // ✅ Block completion without attendance
        if (String(newStatus).toUpperCase() === "COMPLETED") {
            const ok = await hasAnyAttendance(session.sessionId);
            if (ok === null) {
                alert(t("sessions.verifyAttendanceFailed", {
                    defaultValue: "Could not verify attendance. Complete is blocked until this succeeds — refresh and try again.",
                }));
                return;
            }
            if (!ok) {
                alert(t("sessions.markBeforeComplete"));
                return;
            }
        }

        setSavingId(session.sessionId);
        try {
            await setSessionStatus(role, session.sessionId, newStatus);
            // reflect
            setRows(prev => prev.map(r => r.sessionId === session.sessionId ? { ...r, status: newStatus } : r));

            // ✅ Success feedback for COMPLETED status (billing triggered)
            if (String(newStatus).toUpperCase() === "COMPLETED") {
                alert(t("sessions.completeSuccess"));
            }
        } catch (error) {
            console.error("Status change failed:", error);
            const errorMsg = apiErrorMessage(error, t('common.unknownError'));

            // ✅ Specific error message for COMPLETED billing failures
            if (String(newStatus).toUpperCase() === "COMPLETED") {
                alert(t("sessions.completeFailed", { message: errorMsg }));
            } else {
                alert(t("sessions.statusUpdateFailed", { message: errorMsg }));
            }

            // Revert the draft status on error
            setStatusDraft(prev => ({ ...prev, [session.sessionId]: session.status }));
        } finally {
            setSavingId(null);
        }
    };

    // Edit/Create support
    const startCreate = () => { setEditing(null); setShowForm(true); };
    const startEdit = (row) => { setEditing(row); setShowForm(true); };

    const handleSubmit = async (payloadOrArray, recurringRange) => {
        const base = baseFor(role);

        // Handle array of payloads (recurring sessions with selected days)
        if (Array.isArray(payloadOrArray)) {
            const payloads = payloadOrArray;
            for (const payload of payloads) {
                const body = { ...payload };
                if (!body.batchId && batchId) body.batchId = Number(batchId);
                await API.post(`${base}/sessions`, body);
            }
            setShowForm(false);
            // Reload sessions using the ref function to respect filters and pagination
            if (loadSessionsRef.current) {
                setTimeout(() => loadSessionsRef.current(), 500);
            }
        } else {
            // Handle single payload (edit mode)
            const body = { ...payloadOrArray };
            if (!body.batchId && batchId) body.batchId = Number(batchId);

            if (editing && editing.sessionId) {
                await API.put(`${base}/sessions/${editing.sessionId}`, body);
            } else {
                await API.post(`${base}/sessions`, body);
            }
            setShowForm(false);
            // Reload sessions using the ref function to respect filters and pagination
            if (loadSessionsRef.current) {
                setTimeout(() => loadSessionsRef.current(), 500);
            }
        }
    };

    const handleReverse = (session) => {
        setSelectedSession(session);
        setReversalReason("");
        setShowReversalModal(true);
    };

    const confirmReverse = async () => {
        if (!reversalReason.trim()) {
            alert(t("sessions.reversalReasonRequired"));
            return;
        }

        if (!await showConfirm(t("sessions.reversalConfirm"))) {
            return;
        }

        setReversingId(selectedSession.sessionId);
        try {
            await reverseSession(role, selectedSession.sessionId, reversalReason.trim());
            setRows(prev => prev.map(r =>
                r.sessionId === selectedSession.sessionId ? { ...r, status: "CANCELLED" } : r
            ));
            setStatusDraft(prev => ({ ...prev, [selectedSession.sessionId]: "CANCELLED" }));
            setShowReversalModal(false);
            setSelectedSession(null);
            setReversalReason("");
            alert(t("sessions.reversalSuccess"));
        } catch (e) {
            console.error("Reversal failed", e);
            alert(t("sessions.reversalFailed", {
                message: apiErrorMessage(e, t('common.unknownError')),
            }));
        } finally {
            setReversingId(null);
        }
    };

    const handleUpdateSessionNotes = (session) => {
        setSessionForNotesUpdate(session);
        setShowUpdateNotesModal(true);
    };

    const handleNotesUpdateSuccess = () => {
        // Reload sessions after updating notes
        if (loadSessionsRef.current) {
            loadSessionsRef.current();
        }
    };

    const canReverse = role === "ADMIN";

    return (
        <div className="list-page">
            <PageHeaderSection
                title={t("sessions.title")}
                subtitle={t("sessions.subtitle")}
                aside={(
                    <>
                    <button
                        type="button"
                        className="btn-page-action-teal whitespace-nowrap"
                        onClick={() => setShowWalkInModal(true)}
                        title={t("sessions.addWalkInTitle")}
                    >
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                        {t("sessions.addWalkIn")}
                    </button>
                    <button
                        type="button"
                        className="btn-page-action-accent whitespace-nowrap"
                        onClick={() => setShowIndividualModal(true)}
                        title={t("sessions.addIndividualTitle")}
                    >
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {t("sessions.addIndividual")}
                    </button>
                    <button
                        type="button"
                        className="btn-page-action-info whitespace-nowrap"
                        onClick={startCreate}
                        disabled={!batchId}
                        title={!batchId ? t("sessions.selectBatchTitle") : t("sessions.addBatchTitle")}
                    >
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        {t("sessions.addBatch")}
                    </button>
                    </>
                )}
            />

            {dateFilter && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800">
                    <span>
                        {t("sessions.showingOnDate")}{" "}
                        <strong>
                            {(() => {
                                try {
                                    const [y, m, d] = dateFilter.split("-").map(Number);
                                    return formatDate(new Date(y, m - 1, d));
                                } catch { return dateFilter; }
                            })()}
                        </strong>
                        {!batchId && t("sessions.selectLevelBatch")}
                    </span>
                    <button
                        type="button"
                        onClick={() => setDateFilter(null)}
                        className="shrink-0 text-indigo-600 hover:text-indigo-900 font-medium"
                    >
                        {t("sessions.clearDateFilter")}
                    </button>
                </div>
            )}

            {/* Filters Card */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                {filtersLoadError && (
                    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
                        {t('common.optionsLoadFailed')}
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <SelectInput
                        label={t("sessions.filterLevel")}
                        value={levelId}
                        onChange={(e) => setLevelId(e.target.value)}
                    >
                        <option value="">{t("sessions.selectLevel")}</option>
                        {levels.map(l => {
                            const id = l.levelId ?? l.id;
                            return <option key={id} value={id}>{formatLevelLabel(l)}</option>;
                        })}
                    </SelectInput>
                    <SelectInput
                        label={t("sessions.filterBatch")}
                        value={batchId}
                        onChange={(e) => { setBatchId(e.target.value); setPage(0); }}
                        disabled={!levelId}
                    >
                        <option value="">{levelId ? t("sessions.selectBatch") : t("sessions.selectLevelFirst")}</option>
                        {batches.map(b => {
                            const id = b.batchId ?? b.id;
                            return <option key={id} value={id}>{b.name} (#{id})</option>;
                        })}
                    </SelectInput>
                    <SelectInput
                        label={t("sessions.sessionStatus")}
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                    >
                        <option value="">{t("sessions.allStatuses")}</option>
                        <option value="PENDING">{t("sessions.statusPending")}</option>
                        <option value="COMPLETED">{t("sessions.statusCompleted")}</option>
                        <option value="CANCELLED">{t("sessions.statusCancelled")}</option>
                    </SelectInput>
                    <SelectInput
                        label={t("sessions.sortBy")}
                        value={sortBy}
                        onChange={(e) => { setSortBy(e.target.value); setPage(0); }}
                    >
                        <option value="date-desc">{t("sessions.sortDateDesc")}</option>
                        <option value="date-asc">{t("sessions.sortDateAsc")}</option>
                        <option value="status">{t("sessions.sortStatus")}</option>
                        <option value="id-desc">{t("sessions.sortIdDesc")}</option>
                        <option value="id-asc">{t("sessions.sortIdAsc")}</option>
                    </SelectInput>
                    <div className="relative">
                        <TextInput
                            label={t("sessions.searchSessions")}
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setPage(0); }}
                            placeholder={t("sessions.searchPlaceholder")}
                            className="pl-10"
                        />
                        <svg className="absolute left-3 top-[2.125rem] w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                </div>
                {(query || statusFilter) && (
                    <div className="mt-3 flex items-center justify-between text-sm">
                        <div className="text-gray-600">
                            {t("sessions.showingResults", { count: filtered.length })}
                            {statusFilter && <span className="ml-1">• {t("sessions.statusFilter")} <span className="font-medium">{statusLabel(statusFilter)}</span></span>}
                        </div>
                        <button
                            onClick={() => { setQuery(''); setStatusFilter(''); setSortBy('date-desc'); setPage(0); }}
                            className="text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            {t("sessions.clearFilters")}
                        </button>
                    </div>
                )}
            </div>

            {/* Sessions Cards or Empty State */}
            {error && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {error}
                </div>
            )}
            {!batchId && !dateFilter ? (
                <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-gray-200">
                    <svg className="w-20 h-20 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-lg font-medium text-gray-900">{t("sessions.selectLevelBatchEmpty")}</p>
                    <p className="text-sm text-gray-500 mt-1">{t("sessions.selectLevelBatchHint")}</p>
                </div>
            ) : loading ? (
                <ListLoadingSpinner />
            ) : error ? null : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-gray-200">
                    <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="text-lg font-medium text-gray-900">{t("sessions.noSessions")}</p>
                    <p className="text-sm text-gray-500 mt-1">{t("sessions.noSessionsHint")}</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {pageRows.map((r) => {
                        const coachName = coachMap[r.coachId] ?? r.coachFullName ?? null;
                        const current = r.status ?? "PENDING";
                        const draft = statusDraft[r.sessionId] ?? current;
                        const isCompleted = String(current).toUpperCase() === "COMPLETED";
                        const isCancelled = String(current).toUpperCase() === "CANCELLED";
                        const isWalkIn = r.batchName?.startsWith("Walk-in") ?? false;

                        // Status color mapping
                        const statusColors = {
                            PENDING: "bg-yellow-100 text-yellow-800 border-yellow-200",
                            COMPLETED: "bg-green-100 text-green-800 border-green-200",
                            CANCELLED: "bg-red-100 text-red-800 border-red-200"
                        };

                        return (
                            <div key={r.sessionId} className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                                {/* Header */}
                                <div className="table-header px-6 py-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                    {t("sessions.sessionNumber", { id: r.sessionId })}
                                                </span>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${statusColors[current] || statusColors.PENDING}`}>
                                                    {statusLabel(current)}
                                                </span>
                                                {isWalkIn && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                                                        </svg>
                                                        {t("sessions.walkIn")}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                {formatSessionTiming(r.startTime, r.endTime)}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="px-6 py-5">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-5">
                                        {/* Level */}
                                        <div>
                                            <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                                </svg>
                                                {t("sessions.level")}
                                            </div>
                                            <div className="text-sm font-medium text-gray-900">
                                                <NameWithId name={r.levelName} id={r.levelId} />
                                            </div>
                                        </div>

                                        {/* Batch */}
                                        <div>
                                            <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                </svg>
                                                {t("sessions.batch")}
                                            </div>
                                            <div className="text-sm font-medium text-gray-900 break-words">
                                                <NameWithId name={localizeBatchName(r.batchName, t)} id={r.batchId} />
                                            </div>
                                        </div>

                                        {/* Coach */}
                                        <div>
                                            <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                                {t("sessions.coach")}
                                            </div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {r.coachId ? (
                                                    <UserLink userId={r.coachId} name={coachName} role="COACH" />
                                                ) : (
                                                    <span className="text-gray-400">{t("sessions.notAssigned")}</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Status Control */}
                                        <div>
                                            <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                {t("sessions.statusControl")}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <SelectInput
                                                    className="flex-1 text-sm"
                                                    value={draft}
                                                    onChange={(e) => onChangeStatusDraft(r.sessionId, e.target.value)}
                                                    disabled={isCompleted || isCancelled}
                                                    title={isCompleted ? t("sessions.completedLocked") : isCancelled ? t("sessions.cancelledLocked") : t("sessions.saveStatus")}
                                                >
                                                    {STATUS_OPTIONS.map(op => (
                                                        <option key={op} value={op}>{statusLabel(op)}</option>
                                                    ))}
                                                </SelectInput>
                                                <button
                                                    className="px-3 py-2 rounded-lg bg-gray-800 text-white text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed hover:bg-gray-900 transition-colors"
                                                    onClick={() => onSaveStatus(r)}
                                                    disabled={savingId === r.sessionId || draft === current || isCompleted}
                                                    title={isCompleted ? t("sessions.completedLocked") : t("sessions.saveStatus")}
                                                >
                                                    {savingId === r.sessionId ? t("sessions.saving") : t("sessions.save")}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
                                        {(isCompleted || isCancelled) ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className="btn-action-view"
                                                    onClick={() => gotoView(r)}
                                                    title={isCancelled ? t("sessions.viewAttendanceCancelled") : t("sessions.viewAttendanceCompleted")}
                                                >
                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                    {t("sessions.viewAttendance")}
                                                </button>
                                                {isCompleted && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleUpdateSessionNotes(r)}
                                                        className="btn-action-edit"
                                                        title={t("sessions.updateNotesTitle")}
                                                    >
                                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                        {t("sessions.updateNotes")}
                                                    </button>
                                                )}
                                                {isCompleted && canReverse && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleReverse(r)}
                                                        disabled={reversingId === r.sessionId}
                                                        className="btn-action-permanent-remove disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title={t("sessions.reverseTitle")}
                                                    >
                                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                                        </svg>
                                                        {reversingId === r.sessionId ? t("sessions.reversing") : t("sessions.reverse")}
                                                    </button>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    className={r.attendanceMarked ? 'btn-action-view' : 'btn-action-activate'}
                                                    onClick={() => gotoMark(r)}
                                                    disabled={isCompleted || isCancelled}
                                                    title={
                                                        isCompleted
                                                            ? t("sessions.attendanceCompletedLocked")
                                                            : isCancelled
                                                                ? t("sessions.attendanceCancelledLocked")
                                                                : r.attendanceMarked
                                                                    ? t("sessions.viewAttendanceSession")
                                                                    : t("sessions.markAttendanceTitle")
                                                    }
                                                >
                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                                    </svg>
                                                    {r.attendanceMarked ? t("sessions.viewAttendance") : t("sessions.markAttendance")}
                                                </button>
                                                {(!isCancelled
                                                    && (!isCompleted || r.hasMeeting)
                                                    && (r.hasMeeting || r.classroomEligible !== false)) && (
                                                    <SessionHostAction
                                                        sessionId={r.sessionId}
                                                        role={role}
                                                        classroomEligible={r.classroomEligible}
                                                        hasMeeting={r.hasMeeting}
                                                        meetingStatus={r.meetingStatus}
                                                        status={r.status}
                                                        endTime={r.endTime}
                                                        compact
                                                        onStarted={() => loadSessionsRef.current?.()}
                                                        onEnded={() => loadSessionsRef.current?.()}
                                                    />
                                                )}
                                                {(!isCompleted && !isCancelled && !isWalkIn) && (
                                                    <button
                                                        type="button"
                                                        className="btn-action-edit"
                                                        onClick={() => startEdit(r)}
                                                        title={t("sessions.editTitle")}
                                                    >
                                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                        {t("sessions.edit")}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Pagination */}
            {rows.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="text-sm text-gray-700">
                            {t("sessions.pagination", {
                                start: page * size + 1,
                                end: Math.min((page + 1) * size, totalElements),
                                total: totalElements,
                            })}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                disabled={page === 0}
                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <span className="text-sm text-gray-700">
                                {t("common.pageOf", { current: page + 1, total: totalPages })}
                            </span>
                            <button
                                className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                disabled={page + 1 >= totalPages}
                                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                        <SelectInput
                            label={t("sessions.rowsPerPage")}
                            className="text-sm"
                            value={size}
                            onChange={(e) => { const v = Number(e.target.value); setSize(v); setPage(0); }}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </SelectInput>
                    </div>
                </div>
            )}

            {/* Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="w-full max-w-3xl my-8">
                        <SessionForm
                            role={role}
                            initial={editing}
                            batchIdFixed={editing ? editing.batchId : (batchId ? Number(batchId) : undefined)}
                            onCancel={() => setShowForm(false)}
                            onSubmit={handleSubmit}
                        />
                    </div>
                </div>
            )}

            {/* Reversal Modal */}
            {showReversalModal && selectedSession && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4 text-red-600">
                            ⚠️ {t("sessions.reversalModal.title", { id: selectedSession.sessionId })}
                        </h3>
                        <div className="space-y-4">
                            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                                <p className="font-medium mb-1">{t("sessions.reversalModal.willDo")}</p>
                                <ul className="list-disc list-inside space-y-1 text-xs">
                                    <li>{t("sessions.reversalModal.creditWallets")}</li>
                                    <li>{t("sessions.reversalModal.markCancelled")}</li>
                                    <li>{t("sessions.reversalModal.compensatingTxns")}</li>
                                    <li>{t("sessions.reversalModal.recordReason")}</li>
                                </ul>
                            </div>
                            <TextareaInput
                                label={t("sessions.reversalModal.reasonLabel")}
                                value={reversalReason}
                                onChange={(e) => setReversalReason(e.target.value)}
                                placeholder={t("sessions.reversalModal.reasonPlaceholder")}
                                className="h-24"
                                required
                            />
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={() => {
                                        setShowReversalModal(false);
                                        setSelectedSession(null);
                                        setReversalReason("");
                                    }}
                                    className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
                                    disabled={reversingId !== null}
                                >
                                    {t("common.cancel")}
                                </button>
                                <button
                                    onClick={confirmReverse}
                                    disabled={!reversalReason.trim() || reversingId !== null}
                                    className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {reversingId !== null ? t("sessions.reversing") : t("sessions.reversalModal.confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Walk-in Session Modal */}
            <WalkInSessionModal
                role={role}
                isOpen={showWalkInModal}
                onClose={() => setShowWalkInModal(false)}
                onSuccess={(response) => {
                    // Extract session details from response
                    const sessionId = response?.sessionId || "undefined";
                    const enrollmentId = response?.enrollmentId || "undefined";

                    alert(t("sessions.walkInCreated", { sessionId, enrollmentId }));
                    setShowWalkInModal(false);

                    // Reload sessions
                    if (loadSessionsRef.current) {
                        setTimeout(() => loadSessionsRef.current(), 500);
                    }
                }}
            />

            {/* Individual Session Modal */}
            <IndividualSessionModal
                role={role}
                isOpen={showIndividualModal}
                onClose={() => setShowIndividualModal(false)}
                onSuccess={(response) => {
                    // Extract session details from response
                    const sessionId = response?.sessionId || "undefined";
                    const batchName = localizeBatchName(response?.batchName, t) || t('batches.individualSessions', { defaultValue: 'Individual Sessions' });
                    const studentName = response?.studentName || t('attendance.student', { defaultValue: 'Student' });
                    const chargeAmount = response?.chargeAmount || 0;

                    alert(t("sessions.individualCreated", {
                        sessionId,
                        batch: batchName,
                        student: studentName,
                        charge: formatCurrency(chargeAmount),
                    }));
                    setShowIndividualModal(false);

                    // Reload sessions
                    if (loadSessionsRef.current) {
                        setTimeout(() => loadSessionsRef.current(), 500);
                    }
                }}
            />

            {/* Update Session Notes Modal */}
            <UpdateSessionNotesModal
                isOpen={showUpdateNotesModal}
                onClose={() => {
                    setShowUpdateNotesModal(false);
                    setSessionForNotesUpdate(null);
                }}
                session={sessionForNotesUpdate}
                role={role}
                onSuccess={handleNotesUpdateSuccess}
            />
        </div>
    );
};

export default SessionsList;
